import React, { useState } from "react";

/**
 *        A
 *       / \
 *      B   C
 *     /     \
 *    D       E
 */
const treeData = {
  value: "A",
  children: [
    {
      value: "B",
      children: [
        {
          value: "D",
          children: []
        }
      ]
    },
    {
      value: "C",
      children: [
        {
          value: "E",
          children: []
        }
      ]
    }
  ]
};
function getBfsSteps(root) {
  const steps = [];
  const visited = new Set();
  const queue = [];

  // Start BFS by visiting the root
  visited.add(root.value);
  queue.push(root);

  steps.push({
    visited: new Set(visited),
    queue: queue.map((node) => node.value),
    current: null,
    message: `Start BFS from node ${root.value}.`
  });

  // BFS Loop
  while (queue.length > 0) {
    const currentNode = queue.shift();

    steps.push({
      visited: new Set(visited),
      queue: queue.map((node) => node.value),
      current: currentNode.value,
      message: `Dequeued node ${currentNode.value}.`
    });

    // Enqueue child nodes if unvisited
    for (const child of currentNode.children) {
      if (!visited.has(child.value)) {
        visited.add(child.value);
        queue.push(child);

        steps.push({
          visited: new Set(visited),
          queue: queue.map((node) => node.value),
          current: currentNode.value,
          message: `Enqueued child node ${child.value}.`
        });
      }
    }
  }

  // Final step: BFS complete
  steps.push({
    visited: new Set(visited),
    queue: [],
    current: null,
    message: "BFS complete."
  });

  return steps;
}

function TreeNode({ node, visited, current }) {
  // Determine background color based on BFS state
  let bgColor = "bg-gray-200";
  if (visited.has(node.value)) bgColor = "bg-green-300";
  if (node.value === current) bgColor = "bg-yellow-400";

  return (
    <div className="flex flex-col items-center">
      {/* Node Box */}
      <div className={`px-4 py-2 rounded shadow ${bgColor} mb-2`}>
        {node.value}
      </div>

      {/* Children (rendered below in a row) */}
      {node.children.length > 0 && (
        <div className="flex space-x-8">
          {node.children.map((child, idx) => (
            <div key={idx} className="flex flex-col items-center">
              {/* A small vertical line to connect parent to child */}
              <div className="w-px h-4 bg-gray-400"></div>
              <TreeNode node={child} visited={visited} current={current} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BFSAnimation() {
  // Generate all BFS steps once (when component mounts)
  const steps = getBfsSteps(treeData);
  const [stepIndex, setStepIndex] = useState(0);

  // Current step data
  const currentStep = steps[stepIndex];
  const { visited, queue, current, message } = currentStep;

  // Handlers for stepping through BFS
  const handlePrevStep = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };
  const handleNextStep = () => {
    if (stepIndex < steps.length - 1) setStepIndex(stepIndex + 1);
  };

  return (
    <div className="p-4 bg-gray-100 dark:bg-gray-900">
      <h4 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
        BFS Tree Animation
      </h4>

      {/* 4.1 Tree Visualization */}
      <div className="flex justify-center mb-6">
        <TreeNode node={treeData} visited={visited} current={current} />
      </div>

      {/* 4.2 Step Controls */}
      <div className="mb-4 space-x-2 text-center">
        <button
          onClick={handlePrevStep}
          disabled={stepIndex === 0}
          className={`px-4 py-2 rounded text-white ${
            stepIndex > 0 ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400"
          }`}
        >
          Previous
        </button>
        <button
          onClick={handleNextStep}
          disabled={stepIndex === steps.length - 1}
          className={`px-4 py-2 rounded text-white ${
            stepIndex < steps.length - 1
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400"
          }`}
        >
          Next
        </button>
      </div>

      {/* 4.3 Step Details */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-md shadow">
        <p className="text-gray-900 dark:text-gray-200">
          <strong>Step {stepIndex + 1} / {steps.length}:</strong> {message}
        </p>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          <strong>Current Node:</strong> {current || "None"}
        </p>
        <p className="text-gray-500 dark:text-gray-400">
          <strong>Visited:</strong> {Array.from(visited).join(", ") || "None"}
        </p>
        <p className="text-gray-500 dark:text-gray-400">
          <strong>Queue:</strong> {queue.join(", ") || "Empty"}
        </p>
      </div>
    </div>
  );
}
